import { RoleName } from '@forgerock/backstage-ui-core';
import { AppSearchParamsInt } from './app-search-params';
import { EverythingSearchResult } from './everything-search-result';
import { EntityType, Group, MediaType } from './search-constants';

export const defaultParams = [ 'tab', 'query', 'page', 'sort', 'tags' ] as (keyof AppSearchParamsInt)[];

export interface Tab {
  id: string;
  name: string;
  mediaTypes: MediaType[];
  entityTypes: EntityType[] | string[];
  group: Group;
  params: (keyof AppSearchParamsInt)[];
  count: number;
  groupBy: keyof EverythingSearchResult;
  readPermissions?: RoleName[];
  sections: Section[];
}

export interface Section {
  id: Group | string;
  name: string;
  icon?: string;
  tab?: Tab;
  layout: SectionLayout;
  showMoreAction: ShowMoreAction;
  initialCount?: number;
  readPermissions?: RoleName[];
}

export enum SectionLayout {
  LIST,
  TILES
}

export enum ShowMoreAction {
  EXPAND,
  TAB
}

export function tab(options: Partial<Tab>): Tab {
  return {
    id: null,
    name: null,
    entityTypes: [],
    mediaTypes: [],
    group: null,
    readPermissions: [ RoleName.everyone ],
    count: 10,
    groupBy: null,
    sections: null,
    ...options,
    params: [ ...defaultParams, ...(options.params ?? []) ]
  };
}

export function section(options: Partial<Section>): Section {
  return {
    id: null,
    name: null,
    icon: null,
    tab: null,
    layout: SectionLayout.TILES,
    showMoreAction: ShowMoreAction.TAB,
    readPermissions: [ RoleName.everyone ],
    ...options
  }
}

const training = tab({
  id: 'training',
  name: 'Training',
  group: Group.TRAINING,
  groupBy: 'entityType',
  count: 48,
  params: [ 'entityType' ],
  sections: [
    section({
      id: 'PATH',
      name: 'Courses',
      layout: SectionLayout.TILES,
      showMoreAction: ShowMoreAction.EXPAND
    }),
    section({
      id: 'PATH_CHAPTER',
      name: 'Modules',
      layout: SectionLayout.TILES,
      showMoreAction: ShowMoreAction.EXPAND
    }),
    section({
      id: 'EXERCISE',
      name: 'Exercises',
      layout: SectionLayout.TILES,
      showMoreAction: ShowMoreAction.EXPAND
    }),
    section({
      id: 'GUIDE',
      name: 'Workbooks',
      layout: SectionLayout.TILES,
      showMoreAction: ShowMoreAction.EXPAND
    }),
    section({
      id: 'LESSON',
      name: 'Lessons',
      layout: SectionLayout.TILES,
      showMoreAction: ShowMoreAction.EXPAND
    })
  ]
});

const software = tab({
  id: 'software',
  name: 'Software',
  group: Group.SOFTWARE,
  groupBy: 'source',
  count: 48,
  params: [ 'source', 'productVersionPath' ],
  sections: [
    section({
      id: 'DOWNLOAD_META',
      name: 'Downloads',
      layout: SectionLayout.TILES,
      showMoreAction: ShowMoreAction.EXPAND,
      initialCount: 6
    }),
    section({
      id: 'MARKETPLACE_ENTRY',
      name: 'Marketplace',
      layout: SectionLayout.TILES,
      showMoreAction: ShowMoreAction.EXPAND,
      initialCount: 6
    })
  ]
});

const videos = tab({
  id: 'videos',
  name: 'Videos',
  group: Group.MULTIMEDIA,
  count: 16,
  params: [ 'source', 'entityType', 'library', 'library' ]
});

const all = tab({
  id: 'all',
  name: 'All',
  params: [ 'library', 'publicationType', 'source', 'productVersionPath', 'eosl',
    'priority', 'status', 'project', 'entityType' ],
  count: 4,
  groupBy: 'group',
  sections: [
    section({
      id: Group.MULTIMEDIA,
      name: 'Videos',
      icon: 'videocam',
      tab: videos,
      layout: SectionLayout.TILES,
      showMoreAction: ShowMoreAction.TAB
    }),
    section({
      id: Group.TRAINING,
      name: 'Training',
      icon: 'school',
      tab: training,
      layout: SectionLayout.TILES,
      showMoreAction: ShowMoreAction.TAB
    }),
    section({
      id: Group.SOFTWARE,
      name: 'Software',
      icon: 'code',
      tab: software,
      layout: SectionLayout.TILES,
      showMoreAction: ShowMoreAction.TAB
    })
  ]
});

/**
 * Used to access tabs by ID
 */
export const tabLookup = {
  [ undefined as any ]: all, // fallback to all
  all,
  university: training,
  training,
  videos,
  software,
  downloads: software
};

/**
 * Used show the list of tabs for navigation
 */
export const tabList = [
  all,
  videos,
  training,
  software
];
